import { SYNC_BATCH_SIZE } from '@iculogbook/shared';
import type {
  LocalAnaestheticCase,
  LocalCase,
  LocalCommunication,
  LocalEdtEntry,
  LocalProcedure,
  PullResponse,
  PushResponse,
} from '@iculogbook/shared';
import { onLocalWrite } from '../db/repositories/internal.ts';
import { metaRepository } from '../db/repositories/meta.ts';
import { syncRepository } from '../db/repositories/sync.ts';
import { retryDelayMs } from './backoff.ts';
import { mergePlan } from './merge.ts';

/**
 * The sync engine: drains the outbox to the server and pulls back what other
 * devices wrote.
 *
 * Everything about it is deliberately conservative. It never runs two syncs at
 * once, it never drops an outbox item, and it never blocks the UI — the app is
 * fully usable with this failing permanently, which is the point of writing to
 * Dexie first.
 */

export type SyncOutcome = {
  pushed: number;
  pulled: number;
  /** Ids the server already had a newer copy of. Not a failure. */
  stale: number;
  error: SyncError | null;
};

export type SyncError = 'offline' | 'unauthenticated' | 'clock-skew' | 'server';

type Listener = (outcome: SyncOutcome) => void;

const listeners = new Set<Listener>();

/** Single-flight: a second call while one is running returns the same promise. */
let inFlight: Promise<SyncOutcome> | null = null;

export function onSync(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

async function request<T>(path: string, init?: RequestInit): Promise<T | SyncError> {
  let response: Response;

  try {
    response = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: {
        ...(init?.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...init?.headers,
      },
    });
  } catch {
    return 'offline';
  }

  if (response.ok) return (await response.json()) as T;
  if (response.status === 401) return 'unauthenticated';

  const body = (await response.json().catch(() => null)) as {
    error?: { code?: string };
  } | null;

  return body?.error?.code === 'CLOCK_SKEW' ? 'clock-skew' : 'server';
}

function isError(value: unknown): value is SyncError {
  return (
    value === 'offline' ||
    value === 'unauthenticated' ||
    value === 'clock-skew' ||
    value === 'server'
  );
}

/**
 * Runs one push-then-pull cycle. Safe to call from anywhere at any time: a
 * second call while one is in flight joins the first rather than starting a
 * competing sync, which would have both draining the same outbox items.
 */
export function syncNow(userId: string): Promise<SyncOutcome> {
  inFlight ??= runSync(userId).finally(() => {
    inFlight = null;
  });

  return inFlight;
}

async function runSync(userId: string): Promise<SyncOutcome> {
  const outcome: SyncOutcome = { pushed: 0, pulled: 0, stale: 0, error: null };

  const push = await pushOnce(userId);
  outcome.pushed = push.pushed;
  outcome.stale = push.stale;
  outcome.error = push.error;

  // A push failure means the network or the server is unhappy; pulling would
  // fail the same way. Stop and let the backoff decide when to try again.
  if (outcome.error === null) {
    const pull = await pullOnce(userId);
    outcome.pulled = pull.pulled;
    outcome.error = pull.error;
  }

  for (const listener of listeners) listener(outcome);
  return outcome;
}

async function pushOnce(
  userId: string,
): Promise<{ pushed: number; stale: number; error: SyncError | null }> {
  const batch = await syncRepository.nextBatch(userId, SYNC_BATCH_SIZE);
  if (batch.items.length === 0) return { pushed: 0, stale: 0, error: null };

  const updatedAtById = new Map<string, Date>(
    [
      ...batch.cases,
      ...batch.procedures,
      ...batch.edtEntries,
      ...batch.anaestheticCases,
      ...batch.communications,
    ].map((record) => [record.id, record.updatedAt]),
  );

  const result = await request<PushResponse>('/api/sync/push', {
    method: 'POST',
    body: JSON.stringify({
      deviceId: await metaRepository.getDeviceId(),
      cases: batch.cases.map(toWire),
      procedures: batch.procedures.map(toWire),
      edtEntries: batch.edtEntries.map(toWire),
      anaestheticCases: batch.anaestheticCases.map(toWire),
      communications: batch.communications.map(toWire),
    }),
  });

  if (isError(result)) {
    await syncRepository.recordFailure(batch.items, result);
    return { pushed: 0, stale: 0, error: result };
  }

  /*
   * Accepted and stale are both settled. Stale means the server holds a newer
   * copy, so this device's queued version is obsolete — the next pull brings
   * the newer one down. Leaving it queued would retry it forever.
   */
  const settled = [...result.accepted, ...result.stale];
  await syncRepository.settle(userId, settled, new Date(result.serverTime), updatedAtById);

  return { pushed: result.accepted.length, stale: result.stale.length, error: null };
}

async function pullOnce(userId: string): Promise<{ pulled: number; error: SyncError | null }> {
  const since = await metaRepository.getLastPulledAt();
  const query = since === undefined ? '' : `?since=${encodeURIComponent(since)}`;

  const result = await request<PullResponse>(`/api/sync/pull${query}`);
  if (isError(result)) return { pulled: 0, error: result };

  const cases = result.cases.map((record) => fromWire<LocalCase>(record));
  const procedures = result.procedures.map((record) => fromWire<LocalProcedure>(record));
  /*
   * Defaulted, not assumed. A server that has not been redeployed since this
   * build shipped returns no `edtEntries` at all, and a client that read
   * `undefined.map` there would break sync entirely — including for the cases
   * and procedures in the same response.
   */
  const edtEntries = (result.edtEntries ?? []).map((record) => fromWire<LocalEdtEntry>(record));
  const anaestheticCases = (result.anaestheticCases ?? []).map((record) =>
    fromWire<LocalAnaestheticCase>(record),
  );
  const communications = (result.communications ?? []).map((record) =>
    fromWire<LocalCommunication>(record),
  );

  const locals = await syncRepository.localsById([
    ...cases.map((record) => record.id),
    ...procedures.map((record) => record.id),
    ...edtEntries.map((record) => record.id),
    ...anaestheticCases.map((record) => record.id),
    ...communications.map((record) => record.id),
  ]);

  const casePlan = mergePlan(locals, cases);
  const procedurePlan = mergePlan(locals, procedures);
  const edtPlan = mergePlan(locals, edtEntries);
  const anaestheticPlan = mergePlan(locals, anaestheticCases);
  const communicationPlan = mergePlan(locals, communications);

  await syncRepository.applyPulled(
    userId,
    casePlan.write,
    procedurePlan.write,
    edtPlan.write,
    anaestheticPlan.write,
    communicationPlan.write,
  );
  await metaRepository.setLastPulledAt(result.serverTime);

  return {
    pulled:
      casePlan.write.length +
      procedurePlan.write.length +
      edtPlan.write.length +
      anaestheticPlan.write.length +
      communicationPlan.write.length,
    error: null,
  };
}

/** Dates become ISO strings; `syncedAt` is local-only and never sent. */
function toWire(
  record: LocalCase | LocalProcedure | LocalEdtEntry | LocalAnaestheticCase | LocalCommunication,
): Record<string, unknown> {
  const wire: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(record)) {
    if (key === 'syncedAt' || value === undefined) continue;
    wire[key] = value instanceof Date ? value.toISOString() : value;
  }

  return wire;
}

/**
 * The server's copy, as a local record. `syncedAt` is set because this record
 * came from the server by definition — that is what tells the merge on the next
 * pull that there is no pending local edit here.
 */
function fromWire<
  T extends LocalCase | LocalProcedure | LocalEdtEntry | LocalAnaestheticCase | LocalCommunication,
>(record: unknown): T {
  const source = record as Record<string, unknown>;
  const parsed: Record<string, unknown> = { syncedAt: new Date() };

  for (const [key, value] of Object.entries(source)) {
    if (value === null) continue;

    parsed[key] =
      typeof value === 'string' &&
      (key === 'occurredAt' || key === 'createdAt' || key === 'updatedAt' || key === 'deletedAt')
        ? new Date(value)
        : value;
  }

  return parsed as T;
}

/**
 * Starts the background schedule: on app start, when connectivity returns,
 * after any local write, and every five minutes while the tab is visible.
 * Never on a timer while backgrounded — a phone in a pocket has better things
 * to do with its battery.
 */
export function startSyncScheduler(userId: string): () => void {
  let timer: number | undefined;
  let failures = 0;
  let stopped = false;

  const run = (): void => {
    if (stopped || document.visibilityState === 'hidden') return;

    void syncNow(userId).then((outcome) => {
      failures = outcome.error === null ? 0 : failures + 1;
      schedule();
    });
  };

  const schedule = (): void => {
    window.clearTimeout(timer);
    if (stopped) return;

    // Healthy: every five minutes. Failing: exponential backoff from a second.
    const delay = failures === 0 ? 5 * 60 * 1000 : retryDelayMs(failures);
    timer = window.setTimeout(run, delay);
  };

  const onOnline = (): void => {
    failures = 0;
    run();
  };

  const onVisible = (): void => {
    if (document.visibilityState === 'visible') run();
    else window.clearTimeout(timer);
  };

  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);

  /*
   * A local write is the moment an entry exists on this device and nowhere
   * else, so it is the moment most worth trying to send. Debounced, because
   * saving a case writes it and all its procedures in quick succession.
   */
  let writeDebounce: number | undefined;
  const stopWatchingWrites = onLocalWrite(() => {
    window.clearTimeout(writeDebounce);
    writeDebounce = window.setTimeout(run, 250);
  });

  run();

  return () => {
    stopped = true;
    window.clearTimeout(timer);
    window.clearTimeout(writeDebounce);
    stopWatchingWrites();
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
